// 規格品サイズ検索（フリーページ 3 店 × 3 種別 = 9 ページ）。wuSiteResource2 の Shared/Search.ts（AngularJS 1.x の
// ProductSearchApp / MainCtrl）の引き取り先（2026-09-23）。API も OppSites の /Api/SearchKey/ProductSearch から
// wuStorefront の /api/product-size-search へ移した（src/server/product-size-search/）。
//
// ページ本文は現行どおり（<div ng-app="ProductSearchApp" ng-controller="MainCtrl as c"> と、末尾の
// var siteId / typeId / currentPageId / redirectpageUrl）。AngularJS は読み込まず、テンプレートの {{ }} を自前で埋める:
//   - 種別プルダウン [ng-model="c.changePageId"]（option は ng-value=ページ番号）→ 変更で redirectpageUrl + ページ番号 へ遷移
//   - 厚み [ng-model="c.thick"]（"" は全て・完全一致）/ ヨコ [c.w] / タテ [c.h]（値 ± 許容範囲 [c.range]。NaN は無視）
//   - 結果は幅→高さの昇順。tr[ng-repeat] を雛形として {{productInfo.X}} と {{c.calcTax(productInfo.Price)| number:0}} を置換
//   - 税込 = Math.floor(price * 1.1)（現行どおり。税率はフロント直書き）
// ★ 旧 Search.js がまだ読まれているページ（angular.module('ProductSearchApp') が引ける）では何もしない。
//   旧が外れて angular.min.js だけ残っている間は ng-app 属性を外して angular の自動起動（モジュール無しエラー）を止める。
// ★ 種別プルダウンの初期選択: 現行は angular が currentPageId の option を選んでいた。ここでも同じにする
//   （opp-honpo のシート/ブックカバーのページは selected も書いてあるが、fukuro / pack-japan には無い）。

import { resolveDevApiOverride } from '../shared/dev-api-override'

export const PRODUCT_SIZE_SEARCH_API ='https://front.workup-system.com/api/product-size-search'
export const APP_SELECTOR = '[ng-app="ProductSearchApp"]'

export interface ProductSizeItem {
  ProductId: number
  Name: string
  Width: number
  Height: number
  Thick: number
  Price: number
  MinThickness: number
  MaxThickness: number
}

export interface PageConfig {
  siteId: number
  typeId: number
  currentPageId: number
  redirectpageUrl: string
}

/** ページ末尾のグローバル変数。揃っていなければ null */
export function readPageConfig(win: Record<string, unknown>): PageConfig | null {
  const siteId = Number(win.siteId)
  const typeId = Number(win.typeId)
  const currentPageId = Number(win.currentPageId)
  const redirectpageUrl = win.redirectpageUrl
  if (!Number.isInteger(siteId) || !Number.isInteger(typeId) || !Number.isInteger(currentPageId)) return null
  if (typeof redirectpageUrl !== 'string' || redirectpageUrl === '') return null
  return { siteId, typeId, currentPageId, redirectpageUrl }
}

/** 旧 Search.js（AngularJS のモジュール ProductSearchApp）が同じページに居るか */
export function hasOldSearchApp(win: { angular?: { module?: (name: string) => unknown } }): boolean {
  const ng = win.angular
  if (!ng || typeof ng.module !== 'function') return false
  try {
    ng.module('ProductSearchApp')
    return true
  } catch {
    return false // 未登録なら angular が投げる
  }
}

export interface SizeQuery {
  /** select の value そのまま（"NaN" / "" / "160" など） */
  w: string
  h: string
  range: string
  thick: string
}

/** 現行 MainCtrl.search() と同じ絞り込みと並び（幅→高さの昇順・安定） */
export function filterAndSort(list: ProductSizeItem[], q: SizeQuery): ProductSizeItem[] {
  const w = Number(q.w)
  const h = Number(q.h)
  let range = Number(q.range)
  if (isNaN(range)) range = 0
  const thick = Number(q.thick)

  let result = list
  if (!isNaN(w)) result = result.filter((c) => w - range <= c.Width && c.Width <= w + range)
  if (!isNaN(h)) result = result.filter((c) => h - range <= c.Height && c.Height <= h + range)
  if (q.thick !== '' && !isNaN(thick)) result = result.filter((c) => c.Thick == thick)

  return [...result].sort((c, d) => Number(c.Width) - Number(d.Width) || Number(c.Height) - Number(d.Height))
}

export function calcTax(price: number): number {
  return Math.floor(price * 1.1)
}

/** angular の number:0 と同じ 3 桁区切り */
export function formatNumber(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ESCAPES[ch] ?? ch)
}

const TAX_EXPR = /\{\{\s*c\.calcTax\(\s*(\w+)\.Price\s*\)\s*\|\s*number\s*:\s*0\s*\}\}/g
const FIELD_EXPR = /\{\{\s*(\w+)\.(\w+)\s*\}\}/g

/** 雛形（tr の outerHTML）の {{ }} を 1 件ぶん埋める。知らない項目は空にする */
export function renderRow(template: string, item: ProductSizeItem): string {
  return template
    .replace(TAX_EXPR, () => formatNumber(calcTax(item.Price)))
    .replace(FIELD_EXPR, (_m, _alias: string, field: string) => {
      const v = (item as unknown as Record<string, unknown>)[field]
      return v === undefined || v === null ? '' : escapeHtml(String(v))
    })
}

interface Controls {
  typeSelect: HTMLSelectElement | null
  thick: HTMLSelectElement | null
  w: HTMLSelectElement | null
  h: HTMLSelectElement | null
  range: HTMLSelectElement | null
  tbody: Element
  template: string
}

function findControls(root: Element): Controls | null {
  const tpl = root.querySelector('tr[ng-repeat]')
  const tbody = tpl?.parentElement
  if (!tpl || !tbody) return null
  const sel = (model: string): HTMLSelectElement | null =>
    root.querySelector<HTMLSelectElement>(`select[ng-model="${model}"]`)
  return {
    typeSelect: sel('c.changePageId'),
    thick: sel('c.thick'),
    w: sel('c.w'),
    h: sel('c.h'),
    range: sel('c.range'),
    tbody,
    template: tpl.outerHTML.replace(/\s*ng-repeat="[^"]*"/, ''),
  }
}

/** 種別プルダウン: 今のページの option を選び、変更で遷移する */
export function bindTypeSelect(select: HTMLSelectElement, cfg: PageConfig, go: (url: string) => void): void {
  const pageIdOf = (o: HTMLOptionElement): string => o.getAttribute('ng-value') ?? o.value
  for (const o of Array.from(select.options)) {
    if (pageIdOf(o) === String(cfg.currentPageId)) o.selected = true
  }
  select.addEventListener('change', () => {
    const o = select.selectedOptions[0]
    if (!o) return
    const id = pageIdOf(o)
    if (id === '' || id === String(cfg.currentPageId)) return
    go(cfg.redirectpageUrl + id)
  })
}

/**
 * ページに束ねる。一覧は取得前は空で描き、戻り値の関数で差し替える（差し替えのたびに再描画）。
 * 雛形の tr が無ければ null
 */
export function bindSizeSearch(
  root: Element,
  cfg: PageConfig,
  go: (url: string) => void,
): ((list: ProductSizeItem[]) => void) | null {
  const c = findControls(root)
  if (!c) return null
  root.removeAttribute('ng-app') // angular.min.js だけ残っていても自動起動させない

  if (c.typeSelect) bindTypeSelect(c.typeSelect, cfg, go)

  let list: ProductSizeItem[] = []
  const render = (): void => {
    const q: SizeQuery = {
      w: c.w?.value ?? 'NaN',
      h: c.h?.value ?? 'NaN',
      range: c.range?.value ?? '10',
      thick: c.thick?.value ?? '',
    }
    c.tbody.innerHTML = filterAndSort(list, q)
      .map((item) => renderRow(c.template, item))
      .join('')
  }
  for (const s of [c.thick, c.w, c.h, c.range]) s?.addEventListener('change', render)
  render()
  return (next) => {
    list = next
    render()
  }
}

/** 一覧の取り先。?wu_size_api= はローカル検証用の上書きで、localhost / 127.0.0.1 だけ受ける（Issue #103） */
export function apiUrl(cfg: PageConfig, search: string = location.search): string {
  const base = resolveDevApiOverride(search, 'wu_size_api', PRODUCT_SIZE_SEARCH_API)
  return `${base}?siteInfoId=${cfg.siteId}&type=${cfg.typeId}`
}

export default function productSizeSearch(): void {
  const win = window as unknown as Record<string, unknown>
  if (hasOldSearchApp(win as { angular?: { module?: (name: string) => unknown } })) return
  const cfg = readPageConfig(win)
  const root = document.querySelector(APP_SELECTOR)
  if (!cfg || !root) return

  const update = bindSizeSearch(root, cfg, (url) => {
    location.href = url
  })
  if (!update) return

  fetch(apiUrl(cfg), { cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((json: unknown) => {
      if (Array.isArray(json)) update(json as ProductSizeItem[])
    })
    .catch((e) => {
      // 現行も取得失敗は無表示だった。結果表が空のまま（絞り込みの UI は動く）
      console.warn('[wu] product-size-search: 一覧の取得に失敗しました', e)
    })
}
